import { stringify } from 'querystring';
import * as Sentry from '@sentry/browser';
import axios from 'axios';
import base64 from 'base64-js';
import Cookies from 'js-cookie';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import * as moment from 'moment/moment';
import { UAParser } from 'ua-parser-js';
import { Userpilot } from 'userpilot';
import Config from '@/Config';
import Router from '@/Router';
import store from '@/state';
import IntegrationsState from '@/state/IntegrationsState';
import State from '@/state/State';
import { resetStates as resetComposableStates } from '@/state/composables';
import { isRecognitionOnlyClaims } from '@/utils/scopedAdminGate';
import { getPartnership } from '@/utils/partnership';
import { vm } from '@/vm';
import { setDateTimeToUTC } from '@/utils/date';

const localStorageKeyAccessToken = 'accessToken';
const localStorageKeyRefreshToken = 'refreshToken';

// Map an onboarding workflow stage id to its URL path segment. Both questionnaire
// stages share the same endpoint shape; only the segment differs.
const onboardingStageSegment = stageId =>
  stageId === 'client_credit_and_aml_due_diligence' ? 'due-diligence' : 'configuration';

// Serialize array params as repeated bare keys (foo=a&foo=b) rather than axios'
// default foo[]=a, so backends reading request.args.getlist('foo') pick them up.
export const repeatParamsSerializer = params =>
  Object.entries(params)
    .filter(([, v]) => v !== null && v !== undefined)
    .flatMap(([k, v]) => (Array.isArray(v) ? v.map(item => [k, item]) : [[k, v]]))
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

// Pull the filename out of a Content-Disposition header, handling both the
// RFC 5987 filename*=UTF-8''... form and a plain quoted/unquoted filename=...
export const parseContentDispositionFilename = header => {
  if (!header) {
    return undefined;
  }
  const extended = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      return extended[1].trim();
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1].trim() : undefined;
};
// NOTE: for testing bring this down to 2 seconds and set the API JWT expiry to 30 seconds.
const tokenExpiryMarginSeconds = 30;

axios.defaults.responseType = 'json';
axios.defaults.headers.common['X-Client-Partnership'] = getPartnership();
const initialAccessToken = localStorage.getItem(localStorageKeyAccessToken);
const uaParserResult = new UAParser().getResult();
if (initialAccessToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${initialAccessToken}`;
}

function decodeJWT(token) {
  //NOTE: We're only interested in the payload
  //	    the signature will be verified by the API on each request
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    console.error('Bad token: ' + token);
    return null;
  }
}

function isJWTDueToExpireBeforeRequest(token) {
  if (!token) {
    return true;
  }

  const jwt = decodeJWT(token);
  if (!jwt) {
    return true;
  }
  const expirySeconds = jwt.exp;
  if (expirySeconds) {
    const nowSeconds = Math.floor(Date.now() / 1000);
    return nowSeconds >= expirySeconds - tokenExpiryMarginSeconds;
  }

  // No expiry - switch to true to "force" everyone over to new tokens
  return false;
}

function getCompanyIdFromJWT(token) {
  if (!token) {
    return;
  }
  const jwt = decodeJWT(token);
  return (jwt.user_claims || {}).cid;
}

function getJWTFromAxiosRequest(req) {
  //WARNING: axios doesn't define headers under a single 'headers' object
  const authHeader = (((req.headers || {}).common || {}).Authorization || '').split('Bearer ');
  if (Array.isArray(authHeader) && authHeader.length > 0) {
    return authHeader[1];
  }
}

function appendRequestToPostRefreshQueue(req) {
  // Let's attempt to get a new access token as current one has expired
  const refreshToken = getRefreshToken();
  if (!refreshToken || isJWTDueToExpireBeforeRequest(refreshToken)) {
    // Nothing else we can do here
    logout('sessionExpired');
    return false;
  }

  if (!refreshInProgressPromise) {
    // No queue so create new one

    refreshInProgressPromise = new Promise(function (resolve, reject) {
      const refreshData = { requires_admin: true };

      try {
        const companyId = getCompanyIdFromJWT(getToken());
        if (companyId) {
          refreshData['company_id'] = companyId;
        }
      } catch {
        // Do nothing. The above can throw an exception but unclear
        // under what circumstances. Do not need this anyway.
      }

      function refreshSuccess(response) {
        if (response.data && response.data['is_valid'] && response.data['access_token']) {
          // NOTE: Intentionally no reissuance of a refresh token with only a refresh token
          const newAccessToken = response.data['access_token'];

          setToken(newAccessToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;

          refreshInProgressPromise = null;

          //Update token on queued request
          req.headers['Authorization'] = `Bearer ${newAccessToken}`;
          resolve(req);
        } else {
          // not valid or no access token so reject
          reject();
        }
      }

      function refreshError() {
        // not valid or no access token so logout
        logout('sessionExpired');
        throw new axios.Cancel('Session expired');
      }

      // Standardise on axios  for sending refresh requests as most requests use this
      // Otherwise we have two separate paths for refreshing one through fetch one through axios
      axios
        .post(`${Config.apiBaseAuthUrl}/auth/refresh`, JSON.stringify(refreshData), {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
            'Content-Type': 'application/json',
          },
        })
        .then(refreshSuccess)
        .catch(refreshError);
    });

    return refreshInProgressPromise;
  }

  // Refresh in progress so queue request in parallel
  return refreshInProgressPromise.then(() => {
    //Update token on queued request with the freshly-refreshed token
    req.headers['Authorization'] = `Bearer ${getToken()}`;
    return req;
  });
}

// Promise used during both axios and fetch requests
let refreshInProgressPromise = null;

axios.interceptors.request.use(
  function (req) {
    // Avoid intercepting auth routes, otherwise infinite loop
    // Logout will invalidate refresh token so no need to worry if it's expired already
    const ignore = ['/login', '/logout', '/refresh', '/register'];

    if (ignore.filter(e => req.url.indexOf(e) >= 0).length > 0) {
      return req;
    }

    //NOTE: Important to check token attached to axios request not just the default token
    const jwt = getJWTFromAxiosRequest(req) || getToken();

    if (!jwt) {
      return req;
    }

    if (!isJWTDueToExpireBeforeRequest(jwt)) {
      // all fine, pass through
      return req;
    }

    const queuedRequest = appendRequestToPostRefreshQueue(req);

    if (!queuedRequest) {
      throw new axios.Cancel('Session expired');
    }

    return queuedRequest;
  },
  function (error) {
    //Pass any errors through
    return Promise.reject(error);
  },
);

export const getToken = () => {
  return window.localStorage.getItem(localStorageKeyAccessToken);
};

export function setToken(token) {
  window.localStorage.setItem(localStorageKeyAccessToken, token);
}

export const getRefreshToken = () => {
  return window.localStorage.getItem(localStorageKeyRefreshToken);
};

export function setRefreshToken(refreshToken) {
  window.localStorage.setItem(localStorageKeyRefreshToken, refreshToken);
}

export const isLoggedIn = () => {
  return Boolean(getToken());
};

export const logout = async reason => {
  const success = await ApiClient.logout();

  if ((!success || success.status != 205) && reason != 'sessionExpired') {
    console.error('Error logging out, but clearing tokens anyway.');
  }

  window.localStorage.removeItem(localStorageKeyAccessToken);
  window.localStorage.removeItem(localStorageKeyRefreshToken);

  State.logout();
  IntegrationsState.logout();
  store.commit('filters/logout');
  resetComposableStates();

  delete axios.defaults.headers.common['Authorization'];
  // eslint-disable-next-line no-unused-vars
  vm.then(v => {
    // Intercom
    if (v.$intercom) {
      v.$intercom.shutdown();
      v.$intercom.boot({ hide_default_launcher: true });
    }

    // Sentry
    Sentry.configureScope(function (scope) {
      scope.setUser({});
    });
  });

  const params = {};

  if (reason === 'sessionExpired') {
    params.sessionExpired = true;
  }

  Router.push({ name: 'login', params });
};

const setIdleLogoutTimer = () => {
  let time;

  const onIdle = () => {
    clearTimeout(time);
    document.onmousemove = null;
    document.onkeypress = null;
    logout('sessionExpired');
  };

  const resetTimer = () => {
    clearTimeout(time);
    time = setTimeout(onIdle, State.state.idleLogoutTimeInSeconds * 1000);
  };

  window.onload = resetTimer;
  document.onmousemove = resetTimer;
  document.onkeypress = resetTimer;
};

export const handleLogin = async (accessToken, refreshToken) => {
  setToken(accessToken);
  setRefreshToken(refreshToken);

  axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  const user = await ApiClient.getUser();

  if (State.state.idleLogoutTimeInSeconds) {
    setIdleLogoutTimer();
  }

  // eslint-disable-next-line no-unused-vars
  vm.then(v => {
    setTimeout(() => {
      if (!State.state?.company) {
        return;
      }
      // Intercom
      v.$intercom?.boot({
        user_id: State.state.user.user_id,
        user_hash: State.state.credentials.intercom_user_hash,
        name: State.state.user.username,
        email: State.state.user.email,
        company_id: State.state.company.company_id,
        company: {
          name: State.state.company.name,
          id: State.state.company.company_id,
          self_funded: State.state.company.properties.funding_self,
          size: 250,
        },
        self_funded: State.state.company.properties.funding_self,
        hide_default_launcher: true,
      });

      // Userpilot
      Userpilot.identify(State.state.user.user_id, {
        name: State.state.user.username,
        email: State.state.user.email,
        created_at: new Date(State.state.user.created_at).getTime(),
        company: {
          name: State.state.company.name,
          id: State.state.company.company_id,
          created_at: new Date(State.state.company.created_at).getTime(),
        },
      });

      // Sentry
      Sentry.configureScope(function (scope) {
        scope.setUser({
          email: State.state.user.email,
          username: State.state.user.username,
          id: State.state.user.user_id,
          company_name: State.state.company.name,
          company_id: State.state.company.company_id,
        });
      });

      // Heap - if not impersonating
      // if (!State.state.claims.imp) {
      //   window.heap.load('2664699295');
      //   window.heap.identify(State.state.user.user_id);
      //   window.heap.addUserProperties({
      //     email: State.state.user.email,
      //     username: State.state.user.username,
      //     id: State.state.user.user_id,
      //     company_name: State.state.company.name,
      //     company_id: State.state.company.company_id,
      //   });
      // }

      // Front
      // console.log('FrontChat init (ApiClient): '+State.state.user.email+' '+State.state.user.username+' '+State.state.credentials.front_user_hash+' '+State.state.company.name+' '+State.state.company.company_id);
      window.FrontChat?.('init', {
        chatId: 'eff9a91d417917f4d1378bcd9c9443e8',
        useDefaultLauncher: true,
        email: State.state.user.email,
        name: State.state.company.name,
        userHash: State.state.credentials.front_user_hash,
        customFields: {
          company_name: State.state.company.name,
          company_id: State.state.company.company_id,
          user_name: State.state.user.username,
        },
      });

      const partnership = 'wagestream';

      // Elevio
      const groups = [State.state.company.name, State.state.company.partner_state, location.hostname, partnership];

      if (State.state.claims.a) {
        groups.push('Admins');
      }
      if (State.state.claims.m) {
        groups.push('Managers');
      }
      if (State.state.claims.is) {
        groups.push('Infostream Managers');
      }
    }, 2500);
  });
  return user;
};

class ApiError extends Error {
  constructor(message, variables) {
    super(message);
    this.type = 'ApiError';
    this.variables = variables;
  }
}

const handleError = error => {
  const baseContext = {
    page: window.location.href,
    time: new Date().toISOString(),
    companyId: State?.state?.company?.company_id,
    userId: State?.state?.user?.user_id,
    environment: process.env.NODE_ENV,
    baseUrl: Config.apiBaseUrl,
  };

  const formatContext = context =>
    Object.entries(context)
      .map(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          value = JSON.stringify(value, null, 2);
        }
        return `${key}: ${value}`;
      })
      .join('\n');

  let apiError;
  let apiContext;

  if (error?.response) {
    apiContext = {
      ...baseContext,
      message: error?.response?.data?.message || error?.response?.data || error?.message || 'Api Error',
      status: error?.response?.status,
      data: error?.response?.data,
      headers: error?.response?.headers,
      url: error?.config?.url,
    };

    const stringContext = formatContext(apiContext);

    apiError = new ApiError(`Error in ApiClient:\n${stringContext}`);
  } else {
    apiContext = {
      ...baseContext,
      message: error?.message || 'Api Error',
      status: 500,
      data: 'No response data',
      headers: 'No response headers',
      url: error?.config?.url,
    };

    const stringContext = formatContext(apiContext);

    apiError = new ApiError(`Error in ApiClient:\n${stringContext}`);
  }

  // send a rich error to sentry
  Sentry.withScope(scope => {
    scope.setExtra('errorDetails', apiContext);
    Sentry.captureException(apiError);
  });

  // return basic error for the portal to render
  return Promise.reject(
    new ApiError(
      `Request failed: ${error?.response?.data?.message || error?.response?.data || error?.message || 'Api Error'}`,
      {
        ...error?.response?.data,
      },
    ),
  );
};

// If the datetime is returned as UTC from the api, we ensure it has the timezone information correctly
export const readDatetimeAsUTC = datetime => {
  return datetime ? datetime + 'Z' : '';
};

export const portalEnvs = {
  'dashboard.wagestream.com': 'prod',
  'dashboard.uk.stream.co': 'prod',
  'sme.wagestream.com': 'prod',
  'partners.wagestream.co.uk': 'prod',
  'partners.wagestream.com': 'prod',
  'portal.wagestream.com': 'prod',
  'portal.uk.stream.co': 'prod',
  'portal.staging.wagestream.xyz': 'staging',
  'portal.myviewpn.com': 'prod',
  'portal.staging.myviewpn.com': 'staging',
  'portal.financialwellbeing.webitrent.com': 'prod',
  'portal.financialwellbeing.people-first.com': 'prod',
  'sme.wagestream.us': 'prod',
  'sme.staging.wagestream.us': 'prod',
  'portal.wagestream.us': 'prod',
  'dashboard.us.stream.co': 'prod',
  'portal.staging.wagestream.us': 'prod',
};

// Optional analytics date-range window. Buckets are anchored to the start of
// their period (1st of month / Monday of week), so a mid-bucket date would
// otherwise drop the bucket the user selected into. We snap the range out to
// whole buckets for the active period so the selected month/week is included.
const DATE_RANGE_SNAP_UNIT = { m2m: 'month', w2w: 'isoWeek' };
const dateRangeParams = (period, from, to) => {
  const unit = DATE_RANGE_SNAP_UNIT[period];
  const start = from && unit ? moment(from).startOf(unit).format('YYYY-MM-DD') : from;
  const end = to && unit ? moment(to).endOf(unit).format('YYYY-MM-DD') : to;
  return `${start ? `&from=${start}` : ''}${end ? `&to=${end}` : ''}`;
};

const ApiClient = {
  async login(payload) {
    const formData = new FormData();
    if (payload.username) {
      formData.append('username', payload.username);
    }
    if (payload.password) {
      formData.append('password', payload.password);
    }
    if (payload['2fa_pin']) {
      formData.append('2fa_pin', payload['2fa_pin']);
    }
    if (payload['2fa_id']) {
      formData.append('2fa_id', payload['2fa_id']);
    }

    if (payload['company_id']) {
      formData.append('company_id', payload['company_id']);
    }

    formData.append('requires_admin', true);
    payload = { ...payload, requires_admin: true };

    try {
      const response = await axios.post(`${Config.apiBaseAuthUrl}/auth/login`, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Device-Id': payload.deviceId,
          'X-Device-Platform': 'BROWSER',
          'X-Device-Model': uaParserResult.browser.name || 'unknown',
          'X-Device-SystemVersion': uaParserResult.browser.version || 'unknown',
          'X-Application-Id': 'com.wagestream.sme-portal',
        },
      });
      const data = response?.data;

      if (!data) {
        throw 'Invalid response';
      }

      if (data.is_valid) {
        return handleLogin(data.access_token, data.refresh_token);
      }
      if (data.requires_company) {
        throw {
          requires_company: true,
          companies: data.companies,
          message: 'Company ID required',
        };
      }
      if (data.mfa_id) {
        throw {
          requires_mfa: true,
          mfa_id: data.mfa_id,
          message: 'MFA pin required',
        };
      }
      throw {
        message: payload['2fa_id'] ? 'Invalid PIN' : 'Invalid credentials',
      };
    } catch (error) {
      const data = error?.response?.data;

      if (data?.requires_elevation) {
        const deviceId = Cookies.get('wagestream_device_id');
        const elevation = await this.getElevation(data.elevation_id, deviceId);

        if (elevation.attempts_remaining === 0) {
          throw {
            message: 'Number of 2FA attempts exceeded. Please try to log in again.',
          };
        }

        throw {
          requires_elevation: true,
          elevation_id: elevation.elevation_id,
          elevation_strategy: elevation.elevation_strategy,
          elevation_attempts_remaining: elevation.attempts_remaining,
        };
      }

      throw error;
    }
  },
  async logout() {
    const refreshToken = getRefreshToken();
    return axios
      .post(`${Config.apiBaseAuthUrl}/auth/logout`, JSON.stringify('{}'), {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
          'Content-Type': 'application/json',
        },
      })
      .catch(() => {
        return false;
      });
  },
  forgotPassword(email) {
    return axios
      .put(`${Config.apiBaseAuthUrl}/auth/forgot`, { email: email })
      .then(response => response.data, handleError);
  },
  resetPassword(password, reset_id, reset_token) {
    return axios.put(`${Config.apiBaseAuthUrl}/auth/forgot`, {
      password: password,
      reset_id: reset_id,
      token: reset_token,
    });
  },

  createChallenge2fa(userId) {
    return axios
      .post(`${Config.apiBaseAuthUrl}/auth/challenge-2fa`, { user_id: userId })
      .then(response => response.data, handleError);
  },
  getElevation(elevationId, deviceId) {
    return axios
      .get(`${Config.apiBaseAuthUrl}/auth/elevations/${elevationId}`, {
        headers: {
          'X-Device-Id': deviceId,
        },
      })
      .then(response => response.data);
  },
  async checkElevation(elevationId, pin, deviceId) {
    const { data } = await axios.post(
      `${Config.apiBaseAuthUrl}/auth/elevations/${elevationId}`,
      new URLSearchParams({
        response: pin,
      }),
      {
        headers: {
          'X-Device-Id': deviceId,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      },
    );

    if (data.success) {
      return { success: true };
    }

    return { success: false };
  },
  checkChallenge2fa(mfaId, pin) {
    return axios
      .post(`${Config.apiBaseAuthUrl}/auth/verify-2fa`, { mfa_id: mfaId, mfa_pin: pin })
      .then(response => response.data, handleError);
  },

  // Self-service phone enrolment: send an SMS verification code, then verify it.
  // Backend matches the verify request against the texts row by (user_id, mobile, native_device_id,
  // user_agent, otp); user_id only lands in the row if the caller passes it via the `properties`
  // form field (see is_verification_request_valid in us/pubapi/common/phone_numbers.py).
  sendPhoneVerification(mobile, userId) {
    const deviceId = Cookies.get('wagestream_device_id');
    const cleanMobile = mobile.replace(/[^\d+]/g, '');
    return axios
      .post(
        `${Config.apiBaseUrl}/texts/send`,
        new URLSearchParams({
          mobile: cleanMobile,
          type: 'otp-mobile-verification',
          properties: JSON.stringify({ user_id: userId }),
        }),
        {
          headers: { 'X-Device-Id': deviceId, 'Content-Type': 'application/x-www-form-urlencoded' },
        },
      )
      .then(response => response.data, handleError);
  },
  verifyPhone(userId, phoneNumber, code) {
    const deviceId = Cookies.get('wagestream_device_id');
    const cleanPhone = phoneNumber.replace(/[^\d+]/g, '');
    return axios
      .post(
        `${Config.apiBaseUrl}/users/${userId}/phone/verification`,
        { phone_number: cleanPhone, code },
        { headers: { 'X-Device-Id': deviceId } },
      )
      .then(response => response.data, handleError);
  },

  async startOauth2(provider, company_id, next, callback) {
    const url = `${Config.apiBaseUrl}/sme/authorize/${provider}/${company_id}?next=${encodeURIComponent(
      next,
    )}&callback=${encodeURIComponent(callback)}`;
    const resp = await axios.get(url);
    return resp.data;
  },

  async completeOauth2(provider, company_id, code, state, callback) {
    const url = `${Config.apiBaseUrl}/sme/authorize/${provider}/${company_id}?code=${encodeURIComponent(
      code,
    )}&state=${encodeURIComponent(state)}&callback=${encodeURIComponent(callback)}`;
    const resp = await axios.get(url);
    return resp.data;
  },

  async configureIntegration(provider, company_id, payload) {
    const url = `${Config.apiBaseUrl}/sme/configure/${provider}/${company_id}`;
    const resp = await axios.post(url, payload);
    return resp.data;
  },

  async getUser() {
    const response = await axios.get(`${Config.apiBaseAuthUrl}/auth/me`);
    const data = response?.data;

    if (!data) {
      throw 'Invalid response';
    }

    if (!(data.claims.a || data.claims.m || data.claims.is || data.claims.oc || isRecognitionOnlyClaims(data.claims))) {
      //Is SME during setup?
      if (data.tasks && data.tasks.find(x => x.action == 'complete_sme_setup')) {
        data.company = { properties: {} };
        data.paySchedule = {};
      } else {
        throw 'Invalid credentials';
      }
    }

    await State.setMe(data);
    const user = data.user;
    user.company = data.company;
    user.paySchedule = data.pay_schedule;
    user.todos = data.tasks;
    return user;
  },

  patchUser(user, companyId) {
    return axios
      .patch(`${Config.apiBaseUrl}/companyadmins/${companyId}`, user)
      .then(response => response.data, handleError);
  },

  getAllEmployees({ excludeUserData = false } = {}) {
    const params = excludeUserData ? '?exclude_user_data=true' : '';
    return axios.get(`${Config.apiBaseUrl}/reports/employees${params}`).then(response => response.data, handleError);
  },

  getEmployees(page = 0, pageSize = 25, sortColumn = undefined, sortColumnOrder = undefined, searchTerm = undefined) {
    return axios
      .get(
        `${Config.apiBaseUrl}/reports/employees?page=${page}&page_size=${pageSize}&sort_column=${
          sortColumn || ''
        }&sort_column_order=${sortColumnOrder || ''}&q=${searchTerm || ''}`,
      )
      .then(response => {
        return {
          total: response.data.total,
          employees: response.data.data,
        };
      }, handleError);
  },

  getEmployeesToReinvite(companyId) {
    return axios
      .get(`${Config.apiBaseUrl}/comm/self-service/reinvite/employees?company_id=${companyId}`)
      .then(response => response.data, handleError);
  },

  getEmployeeReinviteHtml(companyId) {
    return axios
      .get(`${Config.apiBaseUrl}/emailtemplates/self_service_reinvite/render/${companyId}?skip_sending_email=true`)
      .then(response => response.data, handleError);
  },

  reinviteEmployees(companyId) {
    return axios
      .post(`${Config.apiBaseUrl}/comm/self-service/reinvite/employees?company_id=${companyId}`)
      .then(response => response.data, handleError);
  },

  renderArticle(slug) {
    return axios
      .get(`${Config.apiBaseUrl}/articles/render/${slug}?help_center_type=PORTAL`)
      .then(response => response.data, handleError);
  },

  searchArticles(searchText, excludeSlugs) {
    return axios
      .get(
        `${Config.apiBaseUrl}/articles/search?help_center_type=PORTAL&search_text=${searchText}&exclude_slugs=${excludeSlugs}`,
      )
      .then(response => {
        return response.data;
      }, handleError);
  },

  getPaginatedEmployees(params) {
    const filteredParams = omitBy(params, p => isNull(p) || isUndefined(p));

    return axios
      .get(`${Config.apiBaseUrl}/sme/employees?` + stringify(filteredParams))
      .then(response => response.data, handleError);
  },

  getEmployee(employeeId) {
    return axios.get(`${Config.apiBaseUrl}/employees/${employeeId}`).then(response => response.data.data, handleError);
  },

  getEmployeeBalance(employeeId) {
    return axios
      .get(`${Config.apiBaseUrl}/balances/current/${employeeId}`)
      .then(response => response.data.data, handleError);
  },

  getSmePaySchedule(companyId, payScheduleId) {
    return axios
      .get(`${Config.apiBaseUrl}/sme/pay/${companyId}/${payScheduleId}`)
      .then(response => response.data.data, handleError);
  },

  updateSmePaySchedule(companyId, payScheduleId, paySchedule) {
    return axios
      .post(`${Config.apiBaseUrl}/sme/pay/${companyId}/${payScheduleId}`, paySchedule)
      .then(response => response.data.data, handleError);
  },

  postPayScheduleBulkUpload(payScheduleId, payload) {
    return axios
      .post(`${Config.apiBaseUrl}/payschedules/${payScheduleId}/bulkinsert/dates`, payload)
      .then(response => response.data, handleError);
  },

  createSmePaySchedule(companyId, paySchedule) {
    return axios
      .post(`${Config.apiBaseUrl}/sme/pay/${companyId}`, paySchedule)
      .then(response => response.data.data, handleError);
  },

  getPaySchedules() {
    return axios.get(`${Config.apiBaseUrl}/payschedules/`).then(response => response.data, handleError);
  },

  getCompanyPaySchedules(companyId) {
    return axios
      .get(`${Config.apiBaseUrl}/payschedules/company/${companyId}/used`)
      .then(response => response.data, handleError);
  },

  getAllCompanyPayschedules(companyId) {
    return axios.get(`${Config.apiBaseUrl}/sme/pay/${companyId}`).then(response => response.data, handleError);
  },

  getPayScheduleDates(payScheduleId) {
    return axios
      .get(`${Config.apiBaseUrl}/payschedules/${payScheduleId}/dates`)
      .then(response => response.data.data, handleError);
  },

  getNextPaySchedules(companyId) {
    return axios
      .get(`${Config.apiBaseUrl}/payschedules/${companyId}/next`)
      .then(response => response.data.data, handleError);
  },

  getCompanyFieldPermissions(companyId) {
    return axios
      .get(`${Config.apiBaseUrl}/companies/${companyId}/fieldpermissions`)
      .then(response => response.data.data, handleError);
  },

  getDivisions(employeeId = undefined) {
    let querystring = '';
    if (employeeId) {
      querystring = `?employee_id=${employeeId}`;
    }

    return axios.get(`${Config.apiBaseUrl}/divisions/${querystring}`).then(response => response.data, handleError);
  },
  getDivision(divisionId = undefined) {
    return axios.get(`${Config.apiBaseUrl}/divisions/${divisionId}`).then(response => response.data, handleError);
  },
  addDivision(division) {
    return axios.post(`${Config.apiBaseUrl}/divisions/`, division).then(response => response.data.data, handleError);
  },
  patchDivision(division) {
    return axios
      .patch(`${Config.apiBaseUrl}/divisions/${division.division_id}`, division)
      .then(response => response.data, handleError);
  },
  deleteDivision(divisionId) {
    return axios
      .delete(`${Config.apiBaseUrl}/sme/divisions/${divisionId}`)
      .then(response => response.data.data, handleError);
  },

  getDivisionEmployees(divisionId = undefined) {
    return axios
      .get(`${Config.apiBaseUrl}/divisions/${divisionId}/employees`)
      .then(response => response.data, handleError);
  },

  getDivisionEmployeesCount() {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/divisions/${companyId}/employees/count`)
      .then(response => response.data, handleError);
  },

  getEmployeeDivisionInfo() {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/divisions/${companyId}/employees/info`)
      .then(response => response.data, handleError);
  },

  // Narrow recognition employee endpoints — identity + division fields only,
  // never salary/banking/payroll. Used by the recognition give-award picker
  // and approver/nominator dropdowns so recognition-only admins get a
  // need-to-know surface (see paydays-api /sme/recognition/.../employees*).
  searchRecognitionEmployees({ searchText, divisionIds = [], limit = 10 } = {}) {
    const companyId = State.state.company.company_id;
    const params = omitBy({ q: searchText, limit }, p => isNull(p) || isUndefined(p));
    if (divisionIds.length) {
      params.division_ids = divisionIds.join(',');
    }
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/company/${companyId}/employees/search?${stringify(params)}`)
      .then(response => response.data, handleError);
  },

  listRecognitionEmployees() {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/company/${companyId}/employees`)
      .then(response => response.data, handleError);
  },

  addDivisionEmployee(divisionId, employeeId) {
    return axios
      .post(`${Config.apiBaseUrl}/divisions/${divisionId}/employee`, stringify({ employee_id: employeeId }), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then(response => {
        this.getFilters();
        return response.data.data;
      }, handleError);
  },

  removeDivisionEmployee(divisionId, employeeId) {
    return axios
      .delete(`${Config.apiBaseUrl}/divisions/${divisionId}/employees`, { data: [employeeId] })
      .then(response => {
        this.getFilters();
        return response.data.data;
      }, handleError);
  },

  getDivisionManagers(divisionId = undefined) {
    return axios
      .get(`${Config.apiBaseUrl}/divisions/${divisionId}/managers`)
      .then(response => response.data, handleError);
  },

  postDivisionManager(divisionId = undefined, manager) {
    return axios
      .post(`${Config.apiBaseUrl}/divisions/${divisionId}/managers`, manager)
      .then(response => response.data.data, handleError);
  },

  deleteDivisionManager(divisionId = undefined, manager) {
    return axios
      .delete(`${Config.apiBaseUrl}/divisions/${divisionId}/manager`, { data: { user_id: manager } })
      .then(response => response.data.data, handleError);
  },

  postEmployee(employee) {
    return axios.post(`${Config.apiBaseUrl}/employees/`, employee).then(response => {
      this.getFilters();
      return response.data;
    }, handleError);
  },
  updateEmployee(employee) {
    return axios.patch(`${Config.apiBaseUrl}/employees/${employee.employee_id}`, employee).then(response => {
      this.getFilters();
      return response.data;
    }, handleError);
  },

  updateEmployeeState(employeeId, employeeState) {
    // absolute block on enrolling employees via the portal if not eligible. just in case anyone slips through somehow
    if (employeeState.state === 'ENROLLING') {
      const adminCanEnrollEmployees =
        State.state.company?.properties?.b2b_features?.variations_whitelist?.includes('CAN_ENROLL_EMPLOYEES');
      if (!adminCanEnrollEmployees) {
        throw new Error(
          'You cannot enroll employees via the portal. Employees must be enrolled through your integration.',
        );
      }
    }
    return axios.patch(`${Config.apiBaseUrl}/employees/${employeeId}/state`, employeeState).then(response => {
      this.getFilters();
      return response.data;
    }, handleError);
  },
  getEmployeeHistory(employeeId) {
    return axios
      .get(`${Config.apiBaseUrl}/employees/${employeeId}/history`)
      .then(response => response.data, handleError);
  },

  getEmployeeActivity(employeeId) {
    return axios
      .get(`${Config.apiBaseUrl}/employees/${employeeId}/activity`)
      .then(response => response.data, handleError);
  },

  getEmployeeOptout(employeeId) {
    return axios
      .get(`${Config.apiBaseUrl}/useroptouts/employee/${employeeId}/optout`)
      .then(response => response.data, handleError);
  },

  getEmployeeEnrollable(employeeId) {
    return axios
      .get(`${Config.apiBaseUrl}/employees/${employeeId}/enrollable`)
      .then(response => response.data.enrollable, handleError);
  },

  employeeOptOut(employeeId, optOut) {
    return axios
      .post(`${Config.apiBaseUrl}/useroptouts/employee/${employeeId}/optout`, stringify(optOut), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then(response => {
        this.getFilters();
        return response.data;
      }, handleError);
  },

  employeeOptOutComplete(employeeId) {
    return axios.post(`${Config.apiBaseUrl}/useroptouts/employee/${employeeId}/complete`).then(response => {
      this.getFilters();
      return response.data;
    }, handleError);
  },

  employeeUnenrolmentComplete(employeeId) {
    return axios.patch(`${Config.apiBaseUrl}/unenrolments/complete`, { employee_id: employeeId }).then(response => {
      this.getFilters();
      return response.data;
    }, handleError);
  },

  employeeOptIn(employeeId, reason) {
    return axios
      .post(`${Config.apiBaseUrl}/useroptouts/employee/${employeeId}/reverse`, stringify({ reason: reason }), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then(response => {
        this.getFilters();
        return response.data;
      }, handleError);
  },
  employeeDeactivate(employeeId, reason) {
    return axios
      .post(`${Config.apiBaseUrl}/employees/${employeeId}/deactivate`, stringify({ reason: reason }), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then(response => {
        this.getFilters();
        return response.data;
      }, handleError);
  },

  checkEmployeeEverEnrolled(employeeId) {
    return axios.get(`${Config.apiBaseUrl}/sme/${employeeId}/ever-enrolled`).then(response => {
      return response.data;
    }, handleError);
  },

  employeeSetTerminationDate(employeeId, date) {
    return axios.post(`${Config.apiBaseUrl}/sme/${employeeId}/set-termination-date?date=${date}`).then(response => {
      this.getFilters();
      return response.data;
    }, handleError);
  },

  async postLastDayPay(payload) {
    return axios
      .post(`${Config.apiBaseUrl}/transfers/last-day-pay`, payload)
      .then(response => response.data, handleError);
  },
  async postLastDayPaySettlement(payload) {
    return axios
      .post(`${Config.apiBaseUrl}/transfers/last-day-pay-settlement`, payload)
      .then(response => response.data, handleError);
  },
  downloadLastDayPaySettlementStatement(employeeId, fileName = 'final_statement.pdf') {
    return axios
      .get(`${Config.apiBaseUrl}/transfers/last-day-pay-statement/${employeeId}`, { responseType: 'blob' })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, handleError);
  },
  getLastDayPayReport({ starts_on, ends_on }) {
    const querystring = `?starts_on=${starts_on}&ends_on=${ends_on}`;

    return axios
      .get(`${Config.apiBaseUrl}/reports/last-day-report${querystring}`)
      .then(response => response.data, handleError);
  },

  employeeResetPassword(employeeId, reason, requestedByUserId, email) {
    return axios
      .put(`${Config.apiBaseAuthUrl}/auth/forgot`, {
        employee_id: employeeId,
        requested_by_user_id: requestedByUserId,
        reason: reason,
        email: email,
      })
      .then(response => response.data, handleError);
  },
  employeeInvite(employeeId) {
    return axios
      .post(`${Config.apiBaseUrl}/employees/${employeeId}/invite`)
      .then(response => response.data, handleError);
  },

  getTransfers(params) {
    return axios
      .get(`${Config.apiBaseUrl}/reports/transfers?` + stringify(params))
      .then(response => response.data, handleError);
  },

  getWages(params) {
    return axios
      .get(`${Config.apiBaseUrl}/reports/wages?` + stringify(params))
      .then(response => response.data, handleError);
  },

  getSalarySettlement(params) {
    return axios
      .get(`${Config.apiBaseUrl}/reports/employees-salary-settlement?` + stringify(params))
      .then(response => response.data, handleError);
  },

  getAdoptionData(companyId) {
    return axios.get(`${Config.apiBaseUrl}/sme/adoption/${companyId}`).then(response => response.data, handleError);
  },

  getImpactSavings(companyId) {
    return axios.get(`${Config.apiBaseUrl}/impact/savings/${companyId}`).then(response => response.data, handleError);
  },

  getImpactRetention(companyId) {
    return axios.get(`${Config.apiBaseUrl}/impact/retention/${companyId}`).then(response => response.data, handleError);
  },

  getImpactAdvances(companyId) {
    return axios.get(`${Config.apiBaseUrl}/impact/advances/${companyId}`).then(response => response.data, handleError);
  },

  getImpactCostOfLiving(companyId) {
    return axios
      .get(`${Config.apiBaseUrl}/impact/livingcosts/${companyId}`)
      .then(response => response.data, handleError);
  },

  getAccountingReport(params) {
    let querystring = '';
    if (params) {
      querystring += `?input_date=${params.input_date}`;
    }
    return axios
      .get(`${Config.apiBaseUrl}/reports/accounting` + querystring)
      .then(response => response.data, handleError);
  },

  getEmployeeSalaryStatements(employeeId) {
    const params = {
      starts_on: moment().subtract(10, 'year').format('YYYY-MM-DD'),
      employee_id: employeeId,
      company_id: State.state.company.company_id,
    };
    return axios
      .get(`${Config.apiBaseUrl}/salaries/summary?` + stringify(params))
      .then(response => response.data.data, handleError);
  },

  getEmployeePaymentsReport() {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/reports/employees/payments/${companyId}`)
      .then(response => response.data, handleError);
  },

  downloadEmployeeSalaryStatement(salaryId) {
    return axios
      .get(`${Config.apiBaseUrl}/reports/salaries/${salaryId}/pdf`, { responseType: 'blob' })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'statement.pdf');
        document.body.appendChild(link);
        link.click();
      });
  },

  downloadBankingReport(companyId, fileName) {
    return axios
      .get(`${Config.apiBaseUrl}/companies/integrations/${companyId}/sftp-report`, { responseType: 'blob' })
      .then(response => {
        // The report may be served PGP-encrypted (Content-Type application/pgp-encrypted, a
        // .gpg filename), so honour the server-supplied filename and mimetype rather than
        // assuming plaintext CSV; fall back to the caller's values when they're absent.
        const serverFileName = parseContentDispositionFilename(response.headers['content-disposition']);
        const contentType = response.headers['content-type'] || 'text/csv';
        const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', serverFileName || fileName);
        document.body.appendChild(link);
        link.click();
      });
  },

  getInvalidBankDetails() {
    return axios
      .get(`${Config.apiBaseUrl}/reports/payroll/invalid_bank_details`)
      .then(response => response.data, handleError);
  },
  getInvalidBankDetailsB2b() {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/reports/invalid_bank_details/${companyId}`)
      .then(response => response.data, handleError);
  },

  getFailedSalaries(params) {
    const querystring = `?starts_on=${params.starts_on}&ends_on=${params.ends_on}`;
    return axios
      .get(`${Config.apiBaseUrl}/reports/payroll/failed_salaries` + querystring)
      .then(response => response.data, handleError);
  },

  getMissedSalaries(params) {
    const querystring = `?starts_on=${params.starts_on}&ends_on=${params.ends_on}`;
    return axios
      .get(`${Config.apiBaseUrl}/reports/payroll/missed_salaries` + querystring)
      .then(response => response.data, handleError);
  },

  getReturnedSalaries(params) {
    const querystring = `?starts_on=${params.starts_on}&ends_on=${params.ends_on}`;
    return axios
      .get(`${Config.apiBaseUrl}/reports/payroll/returned-salaries` + querystring)
      .then(response => response.data, handleError);
  },

  getMissedSalariesB2b(params) {
    const companyId = State.state.company.company_id;
    const querystring = `?starts_on=${params.starts_on}&ends_on=${params.ends_on}`;
    return axios
      .get(`${Config.apiBaseUrl}/sme/reports/missed_salaries/${companyId}` + querystring)
      .then(response => response.data, handleError);
  },

  getAdmins() {
    const companyId = State.state.user.company.company_id;
    return axios.get(`${Config.apiBaseUrl}/companyadmins/${companyId}`).then(response => response.data, handleError);
  },

  addAdmin(companyId, admin) {
    return axios
      .post(`${Config.apiBaseUrl}/companyadmins/${companyId}`, admin)
      .then(response => response.data, handleError);
  },

  deleteAdmin(companyId, userId, role) {
    return axios
      .delete(`${Config.apiBaseUrl}/companyadmins/${companyId}`, {
        data: role ? { user_id: userId, role } : { user_id: userId },
      })
      .then(response => response.data, handleError);
  },

  getIntegrations(companyId) {
    IntegrationsState.setLoading();
    return axios.get(`${Config.apiBaseUrl}/companies/integrations/${companyId}`).then(response => {
      IntegrationsState.setIntegrations(response.data.data);
      return response.data;
    }, handleError);
  },

  getIntegrationResults(integrationId) {
    return axios
      .get(`${Config.apiBaseUrl}/companies/integrations/${integrationId}/results`)
      .then(response => response.data, handleError);
  },

  getAvailableSmeIntegrations() {
    return axios.get(`${Config.apiBaseUrl}/sme/integrations`).then(response => {
      return response.data;
    }, handleError);
  },

  postIntegrationTenancy(companyIntegrationId, payload) {
    const companyId = State.state.company.company_id;
    return axios
      .post(`${Config.apiBaseUrl}/sme/integrations/tenancy/${companyId}/${companyIntegrationId}`, payload)
      .then(response => {
        return response.data;
      }, handleError);
  },

  getFilters() {
    store.commit('filters/setLoading');
    return axios.get(`${Config.apiBaseUrl}/dashboards/filters`).then(response => {
      store.commit('filters/setFilters', response.data);
      return response.data;
    }, handleError);
  },

  getRegistration(registrationId) {
    return axios
      .get(`${Config.apiBaseUrl}/registrations/${registrationId}`)
      .then(response => response.data, handleError);
  },

  patchRegistration(registrationId, registration) {
    return axios.patch(`${Config.apiBaseUrl}/registrations/${registrationId}`, registration).then(response => {
      this.getFilters();
      return response;
    }, handleError);
  },

  getRegistrationEmployee(registrationId) {
    return axios
      .get(`${Config.apiBaseUrl}/registrations/${registrationId}/employee`)
      .then(response => response.data, handleError);
  },

  postFile(companyId, fileName, content, metadata) {
    metadata = { ...metadata, source: 'PARTNER_PORTAL' };

    // Note: content should be instance of Uint8Array
    if (!(content instanceof Uint8Array)) {
      throw new Error('File content must be supplied as Uint8Array');
    }

    return axios
      .post(
        `${Config.apiBaseUrl}/companies/integrations/${companyId}/file`,
        stringify({
          file: base64.fromByteArray(content),
          file_name: fileName,
          metadata: JSON.stringify(metadata),
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      )
      .then(
        response => response.data,
        error => {
          if (error.response) {
            throw error.response.data;
          } else {
            throw error;
          }
        },
      );
  },

  // eslint-disable-next-line no-unused-vars
  postClientUpload(companyId, fileName, content, metadata) {
    return axios
      .post(
        `${Config.apiBaseUrl}/companies/${companyId}/client-uploads`,
        stringify({
          file: base64.fromByteArray(content),
          file_name: fileName,
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      )
      .then(
        response => response.data,
        error => {
          if (error.response) {
            throw error.response.data;
          } else {
            throw error;
          }
        },
      );
  },

  validateFile(companyId, fileName, content, entity) {
    // Note: content should be instance of Uint8Array
    if (!(content instanceof Uint8Array)) {
      throw new Error('File content must be supplied as Uint8Array');
    }

    return axios
      .post(
        `${Config.apiBaseUrl}/companies/integrations/${companyId}/file/validate`,
        stringify({
          file: base64.fromByteArray(content),
          file_name: fileName,
          source: 'PARTNER_PORTAL',
          entity: entity,
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      )
      .then(
        response => response.data,
        error => {
          if (error.response) {
            throw error.response.data;
          } else {
            throw error;
          }
        },
      );
  },

  async validateCitransFile(companyId, fileName, content, entity) {
    if (!(content instanceof Uint8Array)) {
      throw new Error('File content must be supplied as Uint8Array');
    }

    try {
      // Create FormData for file upload
      const formData = new FormData();
      const blob = new Blob([content], { type: 'text/csv' });
      formData.append('file', blob, fileName);

      return axios
        .post(
          `${Config.apiBaseUrl}/company_integration_transforms/portal_file_validation/${companyId}/${entity}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        )
        .then(
          response => response.data,
          error => {
            if (error.response) {
              throw error.response.data;
            } else {
              throw error;
            }
          },
        );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  validatePaymentRunCSV(companyId, fileName, content, metadata) {
    metadata = { ...metadata, source: 'SME_PORTAL' };

    if (!(content instanceof Uint8Array)) {
      throw new Error('File content must be supplied as Uint8Array');
    }

    return axios
      .post(
        `${Config.apiBaseUrl}/paywithwagestream/validate/${companyId}`,
        stringify({
          file: base64.fromByteArray(content),
          file_name: fileName,
          metadata: JSON.stringify(metadata),
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      )
      .then(
        response => {
          return {
            ...response.data,
            content, // this is needed to be able to pass on the content to the BE in order to create payment run
          };
        },
        error => {
          if (error.response) {
            throw error.response.data;
          } else {
            throw error;
          }
        },
      );
  },

  uploadPaymentRunCSV(companyId, fileName, content, payImmediate, metadata) {
    metadata = { ...metadata, source: 'SME_PORTAL' };

    if (!(content instanceof Uint8Array)) {
      throw new Error('File content must be supplied as Uint8Array');
    }

    return axios
      .post(
        `${Config.apiBaseUrl}/paywithwagestream/upload/${companyId}`,
        stringify({
          file: base64.fromByteArray(content),
          file_name: fileName,
          pay_immediate: payImmediate,
          metadata: JSON.stringify(metadata),
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      )
      .then(
        response => response.data,
        error => {
          if (error.response) {
            throw error.response.data;
          } else {
            throw error;
          }
        },
      );
  },

  getPaymentRunHistory(companyId) {
    return axios
      .get(`${Config.apiBaseUrl}/paywithwagestream/history/${companyId}`)
      .then(response => response.data, handleError);
  },

  getBankPayHistory(companyId) {
    return axios
      .get(`${Config.apiBaseUrl}/sme/weekly_pay/${companyId}/history`)
      .then(response => response.data.data, handleError);
  },

  deletePaymentRun(companyId, reference) {
    const payload = { reference: reference };
    return axios
      .delete(`${Config.apiBaseUrl}/paywithwagestream/remove/${companyId}`, { data: payload })
      .then(response => response.data, handleError);
  },

  forcePaymentRun(companyId, reference) {
    const payload = { reference: reference };
    return axios
      .post(`${Config.apiBaseUrl}/paywithwagestream/force/${companyId}`, payload)
      .then(response => response.data, handleError);
  },

  postAnalytics(eventName, params) {
    if (process.env.VUE_APP_ANALYTICS_URL) {
      try {
        params = params || {};
        params.environment = portalEnvs[document.location.hostname] || 'dev';
        params.impersonated = State.state?.claims?.imp;
        params.page = window.location.pathname;
        const payload = {
          event: eventName,
          params: params,
          visitor_id: State.state?.visitor?.visitor_id,
          user_id: State.state?.user?.user_id,
          company_id: State.state?.company?.company_id,
          app_id: document.location.hostname,
        };
        return axios.post(process.env.VUE_APP_ANALYTICS_URL, payload).then(response => response.data);
      } catch (error) {
        console.error('Error posting analytics event', error);
      }
    }
  },
  // Dashboard

  async getAppVisits(params) {
    return await axios
      .get(`${Config.apiBaseUrl}/reports/app_visits?${stringify(params)}`)
      .then(response => response.data.data, handleError);
  },

  async getEnrolmentSnapshot(params) {
    return await axios
      .get(`${Config.apiBaseUrl}/dashboards/enrollment_snapshot?${stringify(params)}`)
      .then(response => response.data.data, handleError);
  },

  getReferralCode(companyId) {
    return axios.get(`${Config.apiBaseUrl}/sme/referrals/${companyId}`).then(response => response.data, handleError);
  },

  smeCompanyGetRelevantPeople(companyId) {
    return axios
      .get(`${Config.apiBaseUrl}/compliance/company/gb/${companyId}/relevant_people`)
      .then(response => response.data.data, handleError);
  },
  smeCompanyCreateRelevantPersonConnection(userId, payload) {
    return axios
      .post(`${Config.apiBaseUrl}/compliance/user/gb/${userId}/relevant_person`, payload)
      .then(response => response.data);
  },
  smeCompanyVerifyRelevantPersonConnection(userId, payload) {
    return axios
      .post(`${Config.apiBaseUrl}/compliance/user/gb/${userId}`, payload)
      .then(response => response.data.data, handleError);
  },
  smeInviteRelevantPerson(relevantPersonId, email, companyId) {
    const payload = {
      relevant_person_id: relevantPersonId,
      email: email,
      company_id: companyId,
    };
    return axios
      .post(`${Config.apiBaseUrl}/compliance/user/gb/invite`, payload)
      .then(response => response.data, handleError);
  },
  smeCompanySearch(q) {
    const escapedQ = encodeURIComponent(q);
    return axios
      .get(`${Config.apiBaseUrl}/sme/company/gb/search?q=${escapedQ}`)
      .then(response => response.data.data, handleError);
  },
  smeCompanyGetTaskList(companyId) {
    return axios
      .get(`${Config.apiBaseUrl}/sme/onboarding/${companyId}`)
      .then(response => response.data.data, handleError);
  },
  // Onboarding workflow: lists stages (e.g. Configuration) and their per-
  // company status for the workflow dashboard.
  getOnboardingWorkflow(companyId) {
    return axios
      .get(`${Config.apiBaseUrl}/companies/onboarding/${companyId}/workflow`)
      .then(response => response.data.data, handleError);
  },
  // A questionnaire-shaped stage's content. The two stages share the same shape;
  // stageId selects the URL path segment (configuration vs due-diligence).
  getOnboardingStage(companyId, stageId) {
    const seg = onboardingStageSegment(stageId);
    return axios
      .get(`${Config.apiBaseUrl}/companies/onboarding/${companyId}/stages/${seg}`)
      .then(response => response.data.data, handleError);
  },
  updateOnboardingStageAnswer(companyId, stageId, questionId, value) {
    const seg = onboardingStageSegment(stageId);
    return axios
      .put(`${Config.apiBaseUrl}/companies/onboarding/${companyId}/stages/${seg}/answers/${questionId}`, { value })
      .then(response => response.data.data, handleError);
  },
  updateOnboardingStageSection(companyId, stageId, sectionId, action) {
    const seg = onboardingStageSegment(stageId);
    return axios
      .put(`${Config.apiBaseUrl}/companies/onboarding/${companyId}/stages/${seg}/sections/${sectionId}`, { action })
      .then(response => response.data.data, handleError);
  },
  // Drive a workflow stage's state machine (e.g. release_to_client). Shared across
  // stages; the stageId is the workflow stage id.
  updateOnboardingWorkflowStage(companyId, stageId, action) {
    return axios
      .put(`${Config.apiBaseUrl}/companies/onboarding/${companyId}/workflow/stages/${stageId}`, { action })
      .then(response => response.data.data, handleError);
  },
  // Upload a supporting document (e.g. the certified structure chart) into the
  // existing documents system. contents is base64-encoded file bytes.
  uploadDueDiligenceDocument(companyId, typeCode, { filename, contents, metadata }) {
    return axios
      .post(`${Config.apiBaseUrl}/companies/onboarding/${companyId}/stages/due-diligence/documents/${typeCode}`, {
        filename,
        contents,
        metadata,
      })
      .then(response => response.data.data, handleError);
  },
  // Capture the client's e-signature on the DD form. signatureImage is a PNG data URI;
  // the API records the signing audit trail (IP/user-agent/timestamp) server-side.
  signOnboardingDueDiligence(companyId, { signatureImage, signatoryName }) {
    return axios
      .put(`${Config.apiBaseUrl}/companies/onboarding/${companyId}/stages/due-diligence/signature`, {
        signature_image: signatureImage,
        signatory_name: signatoryName,
      })
      .then(response => response.data.data, handleError);
  },
  // Beneficial owners are written to the canonical company_persons store as BENE_OWNER,
  // so they appear in RiskOps directly. `owner` is the API-shaped record (first_name,
  // last_name, dob, ownership_percentage, address); the API creates the person record.
  addBeneficialOwner(companyId, owner) {
    return axios
      .post(`${Config.apiBaseUrl}/companies/onboarding/${companyId}/stages/due-diligence/beneficial-owners`, owner)
      .then(response => response.data.data, handleError);
  },
  updateBeneficialOwner(companyId, companyPersonId, owner) {
    return axios
      .patch(
        `${Config.apiBaseUrl}/companies/onboarding/${companyId}/stages/due-diligence/beneficial-owners/${companyPersonId}`,
        owner,
      )
      .then(response => response.data.data, handleError);
  },
  removeBeneficialOwner(companyId, companyPersonId) {
    return axios
      .delete(
        `${Config.apiBaseUrl}/companies/onboarding/${companyId}/stages/due-diligence/beneficial-owners/${companyPersonId}`,
      )
      .then(response => response.data, handleError);
  },
  smeEmployeesAddBulk(companyId, fileName, content, metadata) {
    metadata = { ...metadata, source: 'SME_PORTAL' };

    if (!(content instanceof Uint8Array)) {
      throw new Error('File content must be supplied as Uint8Array');
    }

    return axios
      .post(
        `${Config.apiBaseUrl}/sme/${companyId}/employees/bulk`,
        stringify({
          file: base64.fromByteArray(content),
          file_name: fileName,
          metadata: JSON.stringify(metadata),
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      )
      .then(response => response.data, handleError);
  },
  companyFundingBalance(companyId) {
    return axios.get(`${Config.apiBaseUrl}/sme/${companyId}/funding`).then(response => response.data, handleError);
  },
  companyPayrollBalance(companyId) {
    return axios.get(`${Config.apiBaseUrl}/sme/${companyId}/payroll`).then(response => response.data, handleError);
  },

  smeUpdateCompanyProperties(companyId, properties) {
    return axios
      .patch(`${Config.apiBaseUrl}/sme/companies/${companyId}`, properties)
      .then(response => response.data, handleError);
  },

  getCompanyLaunchStatus(companyId) {
    return axios.get(`${Config.apiBaseUrl}/sme/${companyId}/launch`).then(response => response.data, handleError);
  },
  launchCompany(companyId) {
    return axios.post(`${Config.apiBaseUrl}/sme/${companyId}/launch`, {}).then(response => response.data, handleError);
  },
  checkUsername(username) {
    const formData = new FormData();
    formData.append('username', username);
    return axios
      .post(`${Config.apiBaseAuthUrl}/auth/employees`, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then(response => response.data, handleError);
  },
  smeSetup(payload) {
    return axios.post(`${Config.apiBaseUrl}/sme/setup/self-serve`, payload).then(response => {
      if (response.data.auth) {
        setToken(response.data.auth.access_token);
        setRefreshToken(response.data.auth.refresh_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.auth.access_token}`;
      }
      return response.data.data;
    }, handleError);
  },
  smeSubscribe(payload) {
    const companyId = State.state.company.company_id;
    return axios.post(`${Config.apiBaseUrl}/sme/subscribe/${companyId}`, payload).then(response => {
      return response.data.data;
    }, handleError);
  },
  addSubscription(companyId, payload) {
    return axios
      .post(`${Config.apiBaseUrl}/stripe/subscription/${companyId}`, payload)
      .then(response => response.data, handleError);
  },
  getSubscription(companyId) {
    return axios.get(`${Config.apiBaseUrl}/stripe/subscription/${companyId}`).then(response => response.data.data);
  },
  newStripePortalSession(companyId) {
    return axios
      .post(`${Config.apiBaseUrl}/stripe/create-customer-portal-session/${companyId}`)
      .then(response => response.data.data);
  },
  getStripePaymentIntent(companyId) {
    return axios.get(`${Config.apiBaseUrl}/stripe/intent/${companyId}`).then(response => response.data.data);
  },
  postLogSuccessfulStripePaymentIntent(companyId, payload) {
    return axios.post(`${Config.apiBaseUrl}/stripe/intent/${companyId}`, payload).then(response => response.data);
  },
  postCreateStripePaymentCheckoutSession(companyId, payload) {
    return axios
      .post(`${Config.apiBaseUrl}/stripe/create-checkout-session/${companyId}`, payload)
      .then(response => response.data.data);
  },
  getKYCStatus(userId) {
    return axios
      .get(`${Config.apiBaseUrl}/compliance/user/gb/${userId}/kyc_status`)
      .then(response => response.data.data);
  },
  smeGetTerms() {
    return axios.get(`${Config.apiBaseUrl}/sme/terms`).then(res => res.data.data);
  },
  getAgreement(agreementId) {
    return axios.get(`${Config.apiBaseUrl}/agreements/${agreementId}`).then(res => res.data.data, handleError);
  },
  getAcceptedAgreements() {
    const companyId = State.state.company.company_id;
    return axios.get(`${Config.apiBaseUrl}/agreements/company/${companyId}`).then(res => res.data.data, handleError);
  },
  patchUserProperties(payload) {
    const userId = State.state.user.user_id;
    return axios
      .patch(`${Config.apiBaseUrl}/users/${userId}/information`, payload)
      .then(res => res?.data?.data, handleError);
  },
  smeGetPaymentPlans() {
    return axios.get(`${Config.apiBaseUrl}/sme/plans`).then(res => res.data);
  },
  getFundingPosition() {
    return axios.get(`${Config.apiBaseUrl}/reports/position/monthly`).then(response => response.data, handleError);
  },
  getFundingPositionSummary() {
    return axios.get(`${Config.apiBaseUrl}/reports/position/summary`).then(response => response.data, handleError);
  },
  smeGetSelfFundedTransactions(companyId, page = 0, size = 20) {
    return axios
      .get(`${Config.apiBaseUrl}/sme/${companyId}/transactions?page=${page}&size=${size}`)
      .then(res => res.data);
  },
  smeAddSingleShift(shift) {
    const companyId = State.state.company.company_id;
    return axios
      .post(`${Config.apiBaseUrl}/sme/${companyId}/shifts/single`, shift)
      .then(response => response.data, handleError);
  },
  smeAddBulkShifts(companyId, fileName, content, metadata) {
    metadata = { ...metadata, source: 'SME_PORTAL' };

    // Note: content should be instance of Uint8Array
    if (!(content instanceof Uint8Array)) {
      throw new Error('File content must be supplied as Uint8Array');
    }

    return axios
      .post(
        `${Config.apiBaseUrl}/sme/${companyId}/shifts/bulk`,
        stringify({
          file: base64.fromByteArray(content),
          file_name: fileName,
          metadata: JSON.stringify(metadata),
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      )
      .then(
        response => response.data,
        error => {
          if (error.response) {
            throw error.response.data;
          } else {
            throw error;
          }
        },
      );
  },
  smeGetAveragePendingTime() {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/dashboard/pending_time/${companyId}`)
      .then(response => response.data, handleError);
  },
  smeGetShiftsInLastNDays(days) {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/dashboard/last_shift_sync/${companyId}?days=${days}`)
      .then(response => response.data, handleError);
  },
  smeGetEmployeesWithoutShifts(days) {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/dashboard/employees_without_shifts/${companyId}?days=${days}`)
      .then(response => response.data, handleError);
  },
  async smeGetCanAutoEnroll() {
    const companyId = State.state.company.company_id;
    return await axios
      .get(`${Config.apiBaseUrl}/sme/dashboard/can_auto_enroll/${companyId}`)
      .then(response => response.data.data, handleError);
  },
  smeGetEmployeesWithoutPayments() {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/reports/employees_never_receieved_salary/${companyId}`)
      .then(response => response.data, handleError);
  },
  smeGetEmployeesWithSavingsAccounts() {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/dashboard/total_savings/${companyId}`)
      .then(response => response.data, handleError);
  },
  getLaunchState(companyId) {
    return axios.get(`${Config.apiBaseUrl}/sme/${companyId}/launchstate`).then(response => response.data, handleError);
  },
  smeIsPayWith() {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/dashboard/is_pay_with/${companyId}`)
      .then(response => response.data, handleError);
  },
  getNShiftsAfterOffsetAndDeletionEligiblity(params) {
    const companyId = State.state.company.company_id;
    const filteredParams = omitBy(params, p => isNull(p) || isUndefined(p));

    return axios
      .get(`${Config.apiBaseUrl}/sme/${companyId}/shifts?` + stringify(filteredParams))
      .then(response => response.data, handleError);
  },
  getPaginatedShifts(params) {
    const companyId = State.state.company.company_id;
    const filteredParams = omitBy(params, p => isNull(p) || isUndefined(p));

    return axios
      .get(`${Config.apiBaseUrl}/sme/${companyId}/shifts?` + stringify(filteredParams))
      .then(response => response.data, handleError);
  },
  // Operator-filterable shifts list; params carry pre-serialized `field[op]=value` keys.
  getPaginatedShiftsV2(params) {
    const companyId = State.state.company.company_id;
    const filteredParams = omitBy(params, p => isNull(p) || isUndefined(p));

    return axios
      .get(`${Config.apiBaseUrl}/sme/${companyId}/shifts-v2?` + stringify(filteredParams))
      .then(response => response.data, handleError);
  },
  deleteShift(shiftId) {
    return axios.delete(`${Config.apiBaseUrl}/sme/${shiftId}/shifts/delete`);
  },
  smePostWhatsAppOptIn(optIn, userId, agreementId) {
    const companyId = State.state.company.company_id;
    const payload = {
      opt_in: optIn,
      user_id: userId,
      agreement_id: agreementId,
    };
    return axios
      .post(`${Config.apiBaseUrl}/sme/onboarding/${companyId}/whatsapp`, payload)
      .then(response => response.data, handleError);
  },
  smeGetWhatsAppOptIn() {
    const companyId = State.state.company.company_id;
    return axios.get(`${Config.apiBaseUrl}/sme/onboarding/${companyId}/whatsapp`);
  },
  patchSmeEmployeeBanking(employeeId, accountNumber, sortCode, beneficiary_type) {
    const payload = {
      account_number: accountNumber,
      sort_code: sortCode,
    };

    if (beneficiary_type) {
      payload['beneficiary_type'] = beneficiary_type;
    }

    return axios
      .patch(`${Config.apiBaseUrl}/sme/${employeeId}/employees/banking`, payload)
      .then(response => response.data, handleError);
  },
  switchToHourly(employeeId, salaryProperties, date) {
    return axios
      .patch(`${Config.apiBaseUrl}/sme/${employeeId}/shifts/salary_to_hourly?since_date=${date}`, salaryProperties)
      .then(response => response.data, handleError);
  },
  reactivateEmployee(employeeId, payload) {
    const companyId = State.state.company.company_id;
    return axios
      .patch(`${Config.apiBaseUrl}/sme/${companyId}/${employeeId}/reactivate`, payload)
      .then(response => response.data, handleError);
  },
  getWeeklyPayMetrics() {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/metrics/weekly_pay/${companyId}`)
      .then(response => response.data, handleError);
  },
  getAdoptionMetrics(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/adoption/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}&exclude_incomplete=false`,
      )
      .then(response => response.data, handleError);
  },
  getAppFeaturesMetrics(timeSeries, divisionId, includeSectorAverages = false) {
    const companyId = State.state.company.company_id;
    const sectorParam = includeSectorAverages ? '&include_sector_averages=true' : '';
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/app_features/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${sectorParam}`,
      )
      .then(response => response.data, handleError);
  },
  getSectorBenchmarks() {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/metrics/sector-benchmarks/${companyId}`)
      .then(response => response.data, handleError);
  },
  getAppLoginsMetrics(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/app_logins/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getBuildMetrics(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/build/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}&exclude_incomplete=false`,
      )
      .then(response => response.data, handleError);
  },
  getPortalDeposits(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/portal-deposits/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getPortalInterest(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/portal-interest/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getPortalExternalDeposits(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/portal-external-deposits/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getPortalRewardsUsers(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/portal-rewards-users/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getPortalRewardsSavings(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/portal-rewards-savings/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getPortalRewardsAccessed(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/portal-rewards-accessed/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getPortalTrending(divisionId) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/portal-trending/${companyId}${
          divisionId ? `?division_id=${divisionId}` : ''
        }`,
      )
      .then(response => response.data, handleError);
  },
  getPortalTrackBudgetUsers(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/portal-track-budget-users/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getSdlMetrics(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/sdl/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getSdlPurposeMetrics(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/sdl_purpose/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getStreamMetrics(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/stream/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getStreamReasonsMetrics(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/stream_reasons/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getTrackMetrics(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/track/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getShiftCountMetrics(timeSeries, divisionId) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/shift_count/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }`,
      )
      .then(response => response.data, handleError);
  },
  getShiftEmployeesMetrics(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/employee_shifts/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getShiftHoursMetrics(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/hours_by_employee/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getShiftDivisionsMetrics() {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/metrics/divisions/${companyId}`)
      .then(response => response.data, handleError);
  },
  acceptTerms(termsId, notify) {
    const companyId = State.state.company.company_id;
    const userId = State.state.user.user_id;
    const payload = {
      termsId,
      companyId,
      userId,
      notify: notify === undefined ? true : notify,
    };
    return axios.post(`${Config.apiBaseUrl}/sme/terms`, payload).then(response => response.data, handleError);
  },
  setCompanyReturnPaymentBankDetails(payload) {
    const companyId = State.state.company.company_id;
    return axios
      .post(`${Config.apiBaseUrl}/companies/${companyId}/banking`, payload)
      .then(response => response.data, handleError);
  },
  getAuthSwitch() {
    const companyId = State?.state?.company?.company_id;
    // needs to be handled because is called on every page
    // meaning that it will always be called when a user times out and refreshes/ goes to a new page
    if (companyId) {
      return axios
        .get(`${Config.apiBaseAuthUrl}/auth/switch/company/${companyId}`)
        .then(response => response.data, handleError);
    } else {
      return undefined;
    }
  },
  postAuthSwitch(targetCompanyId) {
    const companyId = State.state.company.company_id;
    return axios
      .post(`${Config.apiBaseAuthUrl}/auth/switch/company/${companyId}`, { target_company_id: targetCompanyId })
      .then(response => {
        const data = response.data;
        if (data.is_valid) {
          handleLogin(data.access_token, data.refresh_token);
          window.location.reload();
        }
      }, handleError);
  },
  performEmailAction(token) {
    return axios.get(`${Config.apiBaseUrl}/sme/emailactions/${token}`).then(response => response.data, handleError);
  },
  toggleSalarySignOffEmails(companyId, optIn) {
    const payload = {
      opt_in: optIn,
    };
    return axios
      .post(`${Config.apiBaseUrl}/sme/salary-sign-off/${companyId}/toggle`, payload)
      .then(response => response.data, handleError);
  },
  bulkSalarySignOff(actionId) {
    return axios
      .post(`${Config.apiBaseUrl}/sme/salary-sign-off/portal-confirm/${actionId}`)
      .then(response => response.data, handleError);
  },
  getOffCycleConfig() {
    return axios.get(`${Config.apiBaseUrl}/off-cycle-payments/config`).then(response => response.data, handleError);
  },
  getOffCyclePayments() {
    return axios.get(`${Config.apiBaseUrl}/off-cycle-payments/`).then(response => {
      return {
        ...response.data,
        data: response.data.data.map(item => ({
          ...item,
          created_at: item.created_at + 'Z',
          submitted_at: readDatetimeAsUTC(item.submitted_at),
        })),
      };
    }, handleError);
  },
  getOffCyclePaymentBatch(batchId) {
    return axios.get(`${Config.apiBaseUrl}/off-cycle-payments/groups/${batchId}`).then(response => {
      return {
        ...response.data,
        data: {
          ...response.data.data,
          created_at: response.data.data.created_at + 'Z',
          submitted_at: readDatetimeAsUTC(response.data.data.submitted_at),
          properties: {
            ...response.data.data.properties,
            deferred_payment_datetime: readDatetimeAsUTC(response.data.data.properties?.deferred_payment_datetime),
          },
        },
      };
    }, handleError);
  },
  getOffCyclePaymentbyId(paymentId) {
    return axios
      .get(`${Config.apiBaseUrl}/off-cycle-payments/${paymentId}`)
      .then(response => response.data, handleError);
  },
  deleteOffCycleBatch(batchId) {
    return axios
      .delete(`${Config.apiBaseUrl}/off-cycle-payments/groups/${batchId}`)
      .then(response => response.data, handleError);
  },
  createOffCycleSinglePayment(data) {
    const updatedPayload = {
      ...data,
      properties: {
        ...data.properties,
        deferred_payment_datetime: setDateTimeToUTC(data.properties?.deferred_payment_datetime),
      },
    };

    return axios
      .post(`${Config.apiBaseUrl}/off-cycle-payments/single`, updatedPayload)
      .then(response => response.data, handleError);
  },
  createOffCycleBatch(data) {
    const updatedPayload = {
      ...data,
      properties: {
        ...data.properties,
        deferred_payment_datetime: setDateTimeToUTC(data.properties?.deferred_payment_datetime),
      },
    };

    return axios
      .post(`${Config.apiBaseUrl}/off-cycle-payments/`, updatedPayload)
      .then(response => response.data, handleError);
  },
  saveOffCycleBatch(batchId, data) {
    const updatedPayload = {
      ...data,
      properties: {
        ...data.properties,
        deferred_payment_datetime: setDateTimeToUTC(data.properties?.deferred_payment_datetime),
      },
    };

    return axios
      .put(`${Config.apiBaseUrl}/off-cycle-payments/groups/${batchId}`, updatedPayload)
      .then(response => response.data, handleError);
  },
  processOffCycleBatch(batchId) {
    return axios
      .post(`${Config.apiBaseUrl}/off-cycle-payments/groups/${batchId}/process`)
      .then(response => response.data, handleError);
  },
  downloadOffCycleInvoice(invoiceId) {
    return axios
      .post(
        `${Config.apiBaseUrl}/off-cycle-payments/invoices/${invoiceId}/download_invoice`,
        {},
        { responseType: 'blob' },
      )
      .then(response => response.data, handleError);
  },
  retryOffCyclePayment(payload) {
    return axios
      .post(`${Config.apiBaseUrl}/off-cycle-payments/admin/payments/${payload.off_cycle_payment_id}/retry`, payload)
      .then(response => response.data, handleError);
  },
  hideOffCycleBatch(batchId) {
    return axios
      .post(`${Config.apiBaseUrl}/off-cycle-payments/groups/${batchId}/hide`)
      .then(response => response.data, handleError);
  },
  getOffCycleInvoices() {
    return axios.get(`${Config.apiBaseUrl}/off-cycle-payments/invoices`).then(response => response.data, handleError);
  },
  getDeductionsInvoices() {
    return axios.get(`${Config.apiBaseUrl}/deductions/invoices`).then(response => response.data, handleError);
  },
  downloadDeductionsInvoice(invoiceId) {
    return axios
      .get(`${Config.apiBaseUrl}/deductions/invoices/${invoiceId}/download-invoice`, { responseType: 'blob' })
      .then(response => response.data, handleError);
  },
  downloadDeductionsFile(invoiceId) {
    return axios
      .get(`${Config.apiBaseUrl}/deductions/invoices/${invoiceId}/download-deductions`, { responseType: 'blob' })
      .then(response => response.data, handleError);
  },
  getEmployeeOffCyclePayments(employeeId) {
    return axios
      .get(`${Config.apiBaseUrl}/off-cycle-payments/payments/${employeeId}`)
      .then(response => response.data, handleError);
  },
  uploadImage(image) {
    return axios.post(`${Config.apiBaseUrl}/images/upload/`, image).then(response => response.data, handleError);
  },
  smeGetSalaryHistory(employeeId) {
    return axios.get(`${Config.apiBaseUrl}/sme/${employeeId}/history/salary`).then(response => {
      return response.data;
    }, handleError);
  },
  smeGetExpenseHistory(companyId) {
    return axios
      .get(`${Config.apiBaseUrl}/sme/${companyId}/expenses/history`)
      .then(response => response.data, handleError);
  },
  getAbsencesForCompany(params) {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/absences/company/${companyId}?${stringify(params)}`)
      .then(response => response.data, handleError);
  },
  getJobsForCompany() {
    return axios.get(`${Config.apiBaseUrl}/jobs/`).then(response => response.data.data, handleError);
  },
  getJob(jobId) {
    return axios.get(`${Config.apiBaseUrl}/jobs/${jobId}`).then(response => {
      const job = response.data.data;
      return job;
    }, handleError);
  },
  createJob(job) {
    return axios.post(`${Config.apiBaseUrl}/jobs/`, job).then(response => response.data, handleError);
  },
  updateJob(jobId, job) {
    return axios.patch(`${Config.apiBaseUrl}/jobs/${jobId}`, job).then(response => response.data, handleError);
  },
  deleteJob(jobId) {
    return axios.delete(`${Config.apiBaseUrl}/jobs/${jobId}`).then(response => response.data, handleError);
  },
  postLumpSumSchedule(employeeId, payload) {
    return axios
      .post(`${Config.apiBaseUrl}/sme/${employeeId}/lump-sum`, payload)
      .then(response => response.data, handleError);
  },
  smeUploadExpenses(companyId, fileName, content, metadata) {
    metadata = { ...metadata, source: 'SME_PORTAL' };

    // Note: content should be instance of Uint8Array
    if (!(content instanceof Uint8Array)) {
      throw new Error('File content must be supplied as Uint8Array');
    }

    return axios
      .post(
        `${Config.apiBaseUrl}/sme/${companyId}/expenses/upload`,
        stringify({
          file: base64.fromByteArray(content),
          file_name: fileName,
          metadata: JSON.stringify(metadata),
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      )
      .then(
        response => response.data,
        error => {
          if (error.response) {
            throw error.response.data;
          } else {
            throw error;
          }
        },
      );
  },
  getLastestBrowserUploads(params) {
    const companyId = State.state.user.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/companies/integrations/${companyId}/latest-browser-upload-files?${stringify(params)}`)
      .then(response => response.data, handleError);
  },
  postADPSSOStart(payload) {
    return axios.post(`${Config.apiBaseUrl}/adp/sso/start`, payload).then(response => response.data, handleError);
  },
  async postADPSSOFinish(payload) {
    const resp = await axios.post(`${Config.apiBaseUrl}/adp/sso/finish`, payload);
    if (resp.data.auth) {
      setToken(resp.data.auth.access_token);
      setRefreshToken(resp.data.auth.refresh_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${resp.data.auth.access_token}`;
    }
    return resp.data;
  },
  getRecognitionPrograms({ includeInactive = false } = {}) {
    const companyId = State?.state?.company?.company_id;
    const params = includeInactive ? { include_inactive: 'true' } : {};
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/programs/company/${companyId}`, { params })
      .then(response => response.data.programs, handleError);
  },
  getRecognitionProgram(programId) {
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/programs/${programId}`)
      .then(response => response.data, handleError);
  },
  getRecognitionLedger() {
    const companyId = State?.state?.company?.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/company/${companyId}/ledger`)
      .then(response => response.data, handleError);
  },
  getRecognitionInvoices() {
    const companyId = State?.state?.company?.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/company/${companyId}/invoices`)
      .then(response => response.data, handleError);
  },
  createRecognitionInvoice(data) {
    const companyId = State?.state?.company?.company_id;
    return axios
      .post(`${Config.apiBaseUrl}/sme/recognition/company/${companyId}/invoices`, data)
      .then(response => response.data, handleError);
  },
  deleteRecognitionInvoice(invoiceId) {
    const companyId = State?.state?.company?.company_id;
    return axios
      .delete(`${Config.apiBaseUrl}/sme/recognition/company/${companyId}/invoices/${invoiceId}`)
      .then(response => response.data, handleError);
  },
  getRecognitionUnlinkedDeposits() {
    const companyId = State?.state?.company?.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/company/${companyId}/unlinked-deposits`)
      .then(response => response.data, handleError);
  },
  markRecognitionInvoiceComplete(invoiceId, depositIds) {
    const companyId = State?.state?.company?.company_id;
    return axios
      .post(`${Config.apiBaseUrl}/sme/recognition/company/${companyId}/invoices/${invoiceId}/mark-complete`, {
        deposit_ids: depositIds,
      })
      .then(response => response.data, handleError);
  },
  getRecognitionGalleryImages() {
    const companyId = State?.state?.company?.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/company/${companyId}/images`)
      .then(response => response.data, handleError);
  },
  // Upload a standalone recognition image, returning { s3_key, presigned_url }.
  // imageType is 'banner' or 'logo'. Used by the bulk editor to attach imagery
  // without going through the per-program create/update endpoints.
  uploadRecognitionImage(file, imageType) {
    const companyId = State?.state?.company?.company_id;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('image_type', imageType);
    return axios
      .post(`${Config.apiBaseUrl}/sme/recognition/company/${companyId}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(response => response.data, handleError);
  },
  async createRecognitionProgram(payload, files = {}, s3Keys = {}) {
    // Build multipart form with a JSON part plus optional banner/badge files
    const formData = new FormData();
    const companyId = State?.state?.company?.company_id;

    const body = {
      ...payload,
      ...s3Keys,
      company_id: companyId,
    };

    formData.append('json', JSON.stringify(body));

    if (files?.banner) {
      formData.append('banner', files.banner);
    }
    if (files?.badge) {
      formData.append('badge', files.badge);
    }

    const resp = await axios.post(`${Config.apiBaseUrl}/sme/recognition/programs`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return resp.data;
  },
  async updateRecognitionProgram(programId, payload, files = {}, s3Keys = {}) {
    // Build multipart form with a JSON part plus optional banner/badge files
    const formData = new FormData();

    formData.append('json', JSON.stringify({ ...payload, ...s3Keys }));

    if (files?.banner) {
      formData.append('banner', files.banner);
    }
    if (files?.badge) {
      formData.append('badge', files.badge);
    }

    const resp = await axios.patch(`${Config.apiBaseUrl}/sme/recognition/programs/${programId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return resp.data;
  },
  duplicateRecognitionAwards(payload) {
    const companyId = State?.state?.company?.company_id;
    return axios
      .post(`${Config.apiBaseUrl}/sme/recognition/awards/duplicate`, { ...payload, company_id: companyId })
      .then(response => response.data, handleError);
  },
  getRecognitionAwardGroup(groupId) {
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/award-groups/${groupId}`)
      .then(response => response.data, handleError);
  },
  renameRecognitionAwardGroup(groupId, name) {
    return axios
      .patch(`${Config.apiBaseUrl}/sme/recognition/award-groups/${groupId}`, { name })
      .then(response => response.data, handleError);
  },
  bulkUpdateRecognitionAwardGroupMembers(groupId, members) {
    return axios
      .patch(`${Config.apiBaseUrl}/sme/recognition/award-groups/${groupId}/members`, { members })
      .then(response => response.data, handleError);
  },
  bulkAwardEmployees(programId, payload) {
    return axios
      .post(`${Config.apiBaseUrl}/sme/recognition/programs/${programId}/awards`, payload)
      .then(response => response.data, handleError);
  },
  bulkApproveNominations(nominationIds, amountMinor, recipientMessage, category) {
    return axios
      .patch(`${Config.apiBaseUrl}/recognition/approvals/approve`, {
        nomination_ids: nominationIds,
        amount_minor: amountMinor ?? null,
        recipient_message: recipientMessage || null,
        category: category ?? null,
      })
      .then(response => response.data, handleError);
  },
  bulkRejectNominations(nominationIds) {
    return axios
      .post(`${Config.apiBaseUrl}/recognition/approvals/reject`, {
        nomination_ids: nominationIds,
      })
      .then(response => response.data, handleError);
  },
  withdrawRecognitionGrant(grantId, reason = null) {
    return axios
      .post(`${Config.apiBaseUrl}/sme/recognition/grants/${grantId}/withdraw`, { reason })
      .then(response => response.data.data, handleError);
  },
  getPendingNominations(programId = null) {
    const companyId = State?.state?.company?.company_id;
    const params = {};
    if (programId) {
      params.recognition_award_program_id = programId;
    }
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/company/${companyId}/pending-nominations`, { params })
      .then(response => response.data.nominations, handleError);
  },
  getRecognitionContentReports({ status = null, entityType = null, page = 1, perPage = 50 } = {}) {
    const companyId = State?.state?.company?.company_id;
    const params = { page, per_page: perPage };
    if (status) {
      params.status = status;
    }
    if (entityType) {
      params.entity_type = entityType;
    }
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/company/${companyId}/content-reports`, { params })
      .then(response => response.data.data, handleError);
  },

  getRecognitionContentReportsCount() {
    const companyId = State?.state?.company?.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/company/${companyId}/content-reports/count`)
      .then(response => response.data.data.pending_count, handleError);
  },

  getRecognitionContentReportsByEntity(entityType, entityId) {
    const companyId = State?.state?.company?.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/company/${companyId}/content-reports/${entityType}/${entityId}`)
      .then(response => response.data.data, handleError);
  },

  actionRecognitionContentReport(entityType, entityId, actionType) {
    const companyId = State?.state?.company?.company_id;
    return axios
      .post(
        `${Config.apiBaseUrl}/sme/recognition/company/${companyId}/content-reports/${entityType}/${entityId}/action`,
        { action_type: actionType },
      )
      .then(response => response.data.data, handleError);
  },

  updateRecognitionReportSettings({ autoHide, maxReportsPerDay }) {
    const companyId = State?.state?.company?.company_id;
    // Route through the generic company-properties PATCH rather than duplicating
    // the same endpoint. Send only the keys this function owns (no spread of an
    // existing properties snapshot): the backend deep-merges content_moderation
    // against the latest persisted values, so spreading a (possibly stale) client
    // copy of sibling keys back over it risks clobbering changes made by other
    // admin surfaces.
    return this.smeUpdateCompanyProperties(companyId, {
      properties: {
        content_moderation: {
          hide_reported_by_default: autoHide,
          max_reports_per_day: maxReportsPerDay,
        },
      },
    });
  },

  getUpcomingMilestoneAwards(programId = null, dateTo = null) {
    const companyId = State?.state?.company?.company_id;
    const params = {};
    if (programId) {
      params.recognition_award_program_id = programId;
    }
    if (dateTo) {
      params.date_to = dateTo;
    }
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/company/${companyId}/upcoming-milestone-awards`, { params })
      .then(response => response.data.awards, handleError);
  },
  getLedgerTransactions(params = {}) {
    const companyId = State?.state?.company?.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/company/${companyId}/ledger/payments`, { params })
      .then(response => response.data, handleError);
  },
  getRecognitionActivity(params = {}) {
    const companyId = State?.state?.company?.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/company/${companyId}/activity`, {
        params,
        paramsSerializer: repeatParamsSerializer,
      })
      .then(response => response.data.data, handleError);
  },
  getRecognitionAnalyticsSummary(programId, params = {}) {
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/programs/${programId}/analytics/summary`, { params })
      .then(response => response.data, handleError);
  },
  getRecognitionAnalyticsSpendOverTime(programId, params = {}) {
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/programs/${programId}/analytics/spend-over-time`, { params })
      .then(response => response.data, handleError);
  },
  getRecognitionAnalyticsRedemptionBreakdown(programId, params = {}) {
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/programs/${programId}/analytics/redemption-breakdown`, { params })
      .then(response => response.data, handleError);
  },
  getRecognitionAnalyticsEngagement(programId, params = {}) {
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/programs/${programId}/analytics/engagement`, { params })
      .then(response => response.data, handleError);
  },
  getRecognitionAnalyticsTopRecognised(programId, params = {}) {
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/programs/${programId}/analytics/top-recognised`, { params })
      .then(response => response.data, handleError);
  },
  getRecognitionAnalyticsTopRecognisers(programId, params = {}) {
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/programs/${programId}/analytics/top-recognisers`, { params })
      .then(response => response.data, handleError);
  },
  getRecognitionAnalyticsCompanyValues(programId, params = {}) {
    return axios
      .get(`${Config.apiBaseUrl}/sme/recognition/programs/${programId}/analytics/company-values`, { params })
      .then(response => response.data, handleError);
  },
  getRecognitionAnalyticsValueDistribution(programId, valueName, params = {}, config = {}) {
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/recognition/programs/${programId}/analytics/value-distribution/${encodeURIComponent(
          valueName,
        )}`,
        { params, paramsSerializer: repeatParamsSerializer, cancelToken: config.cancelToken },
      )
      .then(response => response.data, handleError);
  },
  getRecognitionAnalyticsBatch(programIds, params = {}, config = {}) {
    return axios
      .post(
        `${Config.apiBaseUrl}/sme/recognition/programs/analytics/batch`,
        { program_ids: programIds, ...params },
        { cancelToken: config.cancelToken },
      )
      .then(response => response.data, handleError);
  },
  getPortalCoachUsers(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/portal-coach-users/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getPortalCoachChatUsers(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/portal-coach-chat-users/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getPortalCoachBenefits(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/portal-coach-benefits/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getPortalDebtCharity(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/portal-debt-charity/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getPortalCoachCoursesStarted(timeSeries, divisionId, from, to) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/portal-coach-courses-started/${companyId}?period=${timeSeries}${
          divisionId ? `&division_id=${divisionId}` : ''
        }${dateRangeParams(timeSeries, from, to)}`,
      )
      .then(response => response.data, handleError);
  },
  getPortalCoachTopCourses(divisionId, limit = 3) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/portal-coach-top-courses/${companyId}?limit=${limit}${
          divisionId ? `&division_id=${divisionId}` : ''
        }`,
      )
      .then(response => response.data, handleError);
  },
  getPortalCoachFhsDistribution(divisionId) {
    const companyId = State.state.company.company_id;
    return axios
      .get(
        `${Config.apiBaseUrl}/sme/metrics/portal-coach-fhs-distribution/${companyId}${
          divisionId ? `?division_id=${divisionId}` : ''
        }`,
      )
      .then(response => response.data, handleError);
  },
  getPortalSentimentStress(timeSeries) {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/metrics/portal-sentiment-stress/${companyId}?period=${timeSeries}`)
      .then(response => response.data, handleError);
  },
  getPortalSentimentControl(timeSeries) {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/metrics/portal-sentiment-control/${companyId}?period=${timeSeries}`)
      .then(response => response.data, handleError);
  },
  getPortalSentimentEmployer(timeSeries) {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/metrics/portal-sentiment-employer/${companyId}?period=${timeSeries}`)
      .then(response => response.data, handleError);
  },
  getPortalQuotes(page = 1, perPage = 100) {
    const companyId = State.state.company.company_id;
    return axios
      .get(`${Config.apiBaseUrl}/sme/metrics/portal-quotes/${companyId}?page=${page}&per_page=${perPage}`)
      .then(response => response.data, handleError);
  },
};

export default ApiClient;
